import React, { useEffect, useRef, useState } from 'react'
import {
    Button,
    Chip,
    CircularProgress,
    Grid,
    Typography,
} from '@material-ui/core'
import { send_command, ADMIN_GET_APP_CLIENTS } from 'components/api'
import { getApiErrorMessage } from 'utils/getApiErrorMessage'

const COMMANDS = [
    { key: 'SYNCHRONIZE_SPIDS', label: 'Sync SPIDs' },
    { key: 'UPDATE_EMAILS', label: 'Publish Emails' },
    { key: 'GET_EMAILS', label: 'Fetch Emails' },
    { key: 'SYNCHRONIZE_TENANT', label: 'Sync Tenant' },
    { key: 'FETCH_LOGS', label: 'Fetch Logs' },
]

const POLL_INTERVAL_MS = 1500
const POLL_TIMEOUT_MS = 120000

function commandLabel(key) {
    return COMMANDS.find(c => c.key === key)?.label || key
}

function statusTone(status) {
    if (!status) return 'idle'
    if (status === 'Completed') return 'success'
    if (status === 'Failed' || status === 'Cancelled') return 'failed'
    if (status === 'Timed out') return 'failed'
    return 'running'
}

function statusMessage(status, activeCommand) {
    if (!status) return null
    if (status === 'Sending…') return 'Submitting command to the API…'
    if (status === 'Queued — polling…') return `Queued ${commandLabel(activeCommand)} — waiting for the client to pick it up…`
    if (status === 'GET_EMAILS') return 'Client is retrieving emails…'
    if (status === 'Completed') return `${commandLabel(activeCommand)} completed successfully.`
    if (status === 'Failed') return `${commandLabel(activeCommand)} failed.`
    if (status === 'Cancelled') return `${commandLabel(activeCommand)} cancelled while waiting for the client.`
    if (status === 'Timed out') return `${commandLabel(activeCommand)} timed out waiting for the client (2 minutes).`
    return `Command staged: ${status}`
}

export function RemoteConsole({ tenantId, appClientId, onEmailsRetrieved }) {
    const [selectedCmd, setSelectedCmd] = useState(null)
    const [activeCommand, setActiveCommand] = useState(null)
    const [commandStatus, setCommandStatus] = useState(null)
    const [sending, setSending] = useState(false)
    const [error, setError] = useState(null)
    const pollInterval = useRef(null)
    const pollTimeout = useRef(null)
    const pendingGetEmails = useRef(false)

    useEffect(() => {
        return () => {
            if (pollInterval.current) clearInterval(pollInterval.current)
            if (pollTimeout.current) clearTimeout(pollTimeout.current)
        }
    }, [])

    const stopPolling = () => {
        if (pollInterval.current) {
            clearInterval(pollInterval.current)
            pollInterval.current = null
        }
        if (pollTimeout.current) {
            clearTimeout(pollTimeout.current)
            pollTimeout.current = null
        }
    }

    const finishPolling = ({ keepActiveCommand = true } = {}) => {
        stopPolling()
        setSending(false)
        pendingGetEmails.current = false
        if (!keepActiveCommand) setActiveCommand(null)
    }

    const failCommand = (message) => {
        setCommandStatus('Failed')
        setError(message)
        finishPolling()
    }

    const pollCommandState = () => {
        send_command('ADMIN_GET_CURRENT_CLIENT_APP_COMMAND', {
            tenant_id: tenantId,
            app_client_id: appClientId,
        })
            .then(res => {
                if (!res || typeof res === 'string' || res.type == null) {
                    if (pendingGetEmails.current) {
                        return ADMIN_GET_APP_CLIENTS(tenantId)
                            .then(clients => {
                                const client = Array.isArray(clients) ? clients[0] : null
                                if (client?.command_state === 'received') {
                                    setCommandStatus('Completed')
                                    setError(null)
                                    finishPolling()
                                    onEmailsRetrieved?.()
                                } else {
                                    setCommandStatus('GET_EMAILS')
                                }
                            })
                            .catch(ex => {
                                failCommand(getApiErrorMessage(ex, 'Failed while checking email fetch status'))
                            })
                        return
                    }
                    setCommandStatus('Completed')
                    setError(null)
                    finishPolling()
                    return
                }
                setCommandStatus(res.type)
            })
            .catch(ex => {
                failCommand(getApiErrorMessage(ex, 'Lost connection while waiting for the client'))
            })
    }

    const startPolling = () => {
        stopPolling()
        pollInterval.current = setInterval(pollCommandState, POLL_INTERVAL_MS)
        pollTimeout.current = setTimeout(() => {
            setCommandStatus('Timed out')
            setError('The client did not finish this command within 2 minutes.')
            finishPolling()
        }, POLL_TIMEOUT_MS)
        pollCommandState()
    }

    const selectCommand = (key) => {
        if (sending) return
        setSelectedCmd(key)
        setError(null)
    }

    const cancelCommand = () => {
        stopPolling()
        setSending(false)
        pendingGetEmails.current = false
        setCommandStatus('Cancelled')
    }

    const sendClientCommand = () => {
        if (!selectedCmd || sending) return
        setSending(true)
        setError(null)
        setActiveCommand(selectedCmd)
        setCommandStatus('Sending…')

        let apiCall
        if (selectedCmd === 'SYNCHRONIZE_SPIDS') {
            apiCall = send_command('ADMIN_GET_CLIENT_SPIDS', {
                tenant_id: tenantId,
                app_client_id: appClientId,
                command: 'COMMAND_SYNCHRONIZE_SPIDS',
            })
        } else if (selectedCmd === 'UPDATE_EMAILS') {
            apiCall = send_command('ADMIN_SET_CLIENT_UPDATE_EMAILS', {
                tenant_id: tenantId,
                app_client_id: appClientId,
            })
        } else if (selectedCmd === 'GET_EMAILS') {
            pendingGetEmails.current = true
            apiCall = send_command('ADMIN_SET_CLIENT_GET_EMAILS', {
                tenant_id: tenantId,
                app_client_id: appClientId,
            })
        } else if (selectedCmd === 'FETCH_LOGS') {
            apiCall = send_command('ADMIN_GET_CLIENT_LOGS', {
                tenant_id: tenantId,
            })
        } else if (selectedCmd === 'SYNCHRONIZE_TENANT') {
            apiCall = send_command('ADMIN_SET_CLIENT_COMMAND', {
                tenant_id: tenantId,
                app_client_id: appClientId,
                command: { type: 'SYNCHRONIZE_TENANT' },
            })
        }

        apiCall
            .then(() => {
                setCommandStatus('Queued — polling…')
                startPolling()
            })
            .catch(ex => {
                failCommand(getApiErrorMessage(ex, `Failed to send ${commandLabel(selectedCmd)}`))
            })
    }

    const tone = statusTone(commandStatus)
    const statusText = statusMessage(commandStatus, activeCommand)

    return (
        <div className="superadmin-remote-console">
            <Typography variant="body2" color="textSecondary" className="superadmin-remote-console__hint">
                Choose a command, then send it to the client app. The client must be online and heartbeating to respond.
            </Typography>

            <Grid container spacing={1} className="superadmin-remote-console__commands">
                {COMMANDS.map(({ key, label }) => (
                    <Grid item key={key}>
                        <Button
                            variant={selectedCmd === key ? 'contained' : 'outlined'}
                            color={selectedCmd === key ? 'primary' : 'default'}
                            size="small"
                            onClick={() => selectCommand(key)}
                            disabled={sending}
                        >
                            {label}
                        </Button>
                    </Grid>
                ))}
            </Grid>

            <Grid container spacing={1} alignItems="center" className="superadmin-remote-console__actions">
                <Grid item>
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={sendClientCommand}
                        disabled={!selectedCmd || sending}
                    >
                        {sending ? 'Running…' : 'Send command'}
                    </Button>
                </Grid>
                {sending && (
                    <Grid item>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={cancelCommand}
                        >
                            Cancel
                        </Button>
                    </Grid>
                )}
            </Grid>

            {error && (
                <Typography variant="body2" color="error" className="superadmin-remote-console__error">
                    {error}
                </Typography>
            )}

            {statusText && (
                <div className={`superadmin-remote-console__status superadmin-remote-console__status--${tone}`}>
                    {tone === 'running' && (
                        <CircularProgress size={16} className="superadmin-remote-console__spinner" />
                    )}
                    {tone === 'success' && (
                        <Chip size="small" label="Done" className="superadmin-remote-console__chip" />
                    )}
                    {(tone === 'failed') && (
                        <Chip size="small" label="Failed" className="superadmin-remote-console__chip" />
                    )}
                    <Typography variant="body2" component="span">
                        {statusText}
                    </Typography>
                </div>
            )}
        </div>
    )
}
